<template>
    <div class="main-layout__container">
        <ViewCarHeader
            :car="car"
            @delete="handleDeleteCar()"
            @recreate="handleRecreateCar()"
        />

        <UILoader v-if="isCarLoading" class="detailable" />

        <div v-else-if="!isCarLoading && car !== null" class="cars-list-detail__content">
            <div class="cars-list-detail__info">
                <div class="cars-list-detail__gallery">
                    <UIGalleryWithSlider :items="galleryItems" />
                </div>

                <div class="cars-list-detail__description-wrapper">
                    <div class="cars-list-detail__description">
                        <div
                            v-if="car.comment"
                            class="cars-list-detail__description-text"
                        >
                            {{ car.comment ?? '' }}
                        </div>

                        <div class="cars-list-detail__description-list">
                            <div class="cars-list-detail__description-item">
                                <div class="cars-list-detail__description-label">VIN:</div>
                                <div class="cars-list-detail__description-value">{{ car.vin }}</div>
                            </div>

                            <div class="cars-list-detail__description-item">
                                <div class="cars-list-detail__description-label">Тип поступления:</div>
                                <div class="cars-list-detail__description-value">
                                    {{ getCarAdmissionTypeLabel(car.admission_type) }}
                                </div>
                            </div>

                            <div class="cars-list-detail__description-item">
                                <div class="cars-list-detail__description-label">Тип НДС:</div>
                                <div class="cars-list-detail__description-value">
                                    {{ getCarNdsTypeLabel(car.nds_type) }}
                                </div>
                            </div>

                            <div class="cars-list-detail__description-item">
                                <div class="cars-list-detail__description-label">Добавлен:</div>
                                <div class="cars-list-detail__description-value">
                                    {{ formatFromUTC(car.created_at) }}
                                </div>
                            </div>

                            <div class="cars-list-detail__description-item">
                                <div class="cars-list-detail__description-label">Автор:</div>
                                <div class="cars-list-detail__description-value">
                                    {{ car.creator?.name }}
                                </div>
                            </div>

                            <div class="cars-list-detail__description-item">
                                <!-- @TODO здесь будет ссылка на дилера -->
                                <div class="cars-list-detail__description-label">Дилер:</div>
                                <div class="cars-list-detail__description-value link">
                                    {{ getCarDealerFullName(car) }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="cars-list-detail__actions">
                        <div
                            v-if="car.status === CarStatus.Sold"
                            class="cars-list-detail__price"
                        >
                            <div class="cars-list-detail__price-label">Победитель</div>
                            <div class="cars-list-detail__price-value">
                                {{ car.lastAuction?.last_lot?.winner.dealer_name }}
                            </div>
                        </div>

                        <div class="cars-list-detail__price">
                            <div class="cars-list-detail__price-label">Стартовая цена</div>
                            <div class="cars-list-detail__price-value">
                                {{ numberFormat(car.start_price) }} ₽
                            </div>
                        </div>

                        <div
                            v-if="car.status === CarStatus.Sold"
                            class="cars-list-detail__price"
                        >
                            <div class="cars-list-detail__price-label">Ставка победителя</div>
                            <div class="cars-list-detail__price-value">
                                {{ car.lastAuction?.last_lot?.winner.bid_value ? numberFormat(car.lastAuction?.last_lot?.winner.bid_value) : '' }} ₽
                            </div>
                        </div>

                        <div
                            v-if="car.status === CarStatus.OnAuction || car.status === CarStatus.Sold"
                            class="cars-list-detail__price"
                        >
                            <div class="cars-list-detail__price-label">Дата начала</div>
                            <div class="cars-list-detail__price-value">
                                {{ car.lastAuction?.last_lot?.start_date ? formatFromUTC(car.lastAuction.last_lot.start_date) : '' }}
                            </div>
                        </div>

                        <div
                            v-if="car.status === CarStatus.OnAuction || car.status === CarStatus.Sold"
                            class="cars-list-detail__price"
                        >
                            <div class="cars-list-detail__price-label">Дата окончания</div>
                            <div class="cars-list-detail__price-value">
                                {{ car.lastAuction?.last_lot?.end_date ? formatFromUTC(car.lastAuction.last_lot.end_date) : '' }}
                            </div>
                        </div>

                        <div
                            v-if="(canApproveCar(car) || canRejectCar(car))
                                || car.status === CarStatus.OnAuction
                                || car.status === CarStatus.Sold
                            "
                            class="cars-list-detail__buttons"
                        >
                            <UISimpleButton
                                v-if="canApproveCar(car)"
                                @clicked="handleApproveCar"
                            >
                                Согласовать
                            </UISimpleButton>

                            <UISimpleButton
                                v-if="canRejectCar(car)"
                                button-type="outlined"
                                @clicked="handleRejectCar"
                            >
                                Отклонить
                            </UISimpleButton>

                            <router-link
                                v-if="car.status === CarStatus.OnAuction && car.lastAuction?.last_lot"
                                :to="{ name: 'ViewAuction', params: { auction_id: car.lastAuction.last_lot.id } }"
                            >
                                <UISimpleButton>
                                    В аукцион
                                </UISimpleButton>
                            </router-link>

                            <UIDropDown
                                v-if="canViewCarSaleContract(car) || canViewCarInvoice(car)"
                                class="cars-list-detail__documents-dropdown"
                            >
                                <template #trigger>
                                    <UISimpleButton>
                                        <template #icon>
                                            <img v-svg-inline src="@/assets/icons/report.svg" />
                                        </template>

                                        <template #icon-right>
                                            <img v-svg-inline src="@/assets/icons/ui/select-arrow.svg" />
                                        </template>
                                        Документы
                                    </UISimpleButton>
                                </template>

                                <template #elements>
                                    <li
                                        v-if="canViewCarSaleContract(car)"
                                        class="ui-dropdown-menu__element"
                                        @click="onDownloadSaleContract"
                                    >
                                        <img v-svg-inline src="@/assets/icons/ui/document.svg" />
                                        <span>ДКП</span>
                                    </li>

                                    <li
                                        v-if="canViewCarInvoice(car)"
                                        class="ui-dropdown-menu__element"
                                        @click="onDownloadInvoice"
                                    >
                                        <img v-svg-inline src="@/assets/icons/ui/document.svg" />
                                        <span>Счет на оплату</span>
                                    </li>
                                </template>
                            </UIDropDown>
                        </div>
                    </div>
                </div>
            </div>

            <UIAccordionList :items="characteristics">
                <template #content-0>
                    <CarCharacteristics :car="car" />
                </template>

                <template #content-1>
                    <CarEquipment :car="car" />
                </template>

                <template #content-2>
                    <CarDamage :car="car" />
                </template>

                <template #content-3>
                    <CarLegal :car="car" />
                </template>

                <template #content-4>
                    <div class="cars-list-detail__info-documents">
                        <CarDocuments
                            type-title="Диагностическая карта"
                            :car-documents="carDocuments.diagnostic_card"
                            if-no-documents-show-this="file-not-uploaded"
                        />

                        <CarDocuments
                            type-title="Отчет автокод/автотека"
                            :car-documents="carDocuments.avtocod_report"
                            if-no-documents-show-this="file-not-uploaded"
                        />

                        <CarDocuments
                            type-title="Дополнительные документы"
                            :car-documents="carDocuments.document"
                        />
                    </div>
                </template>
            </UIAccordionList>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import noPhotoImage from '@/assets/images/cars/no-photo.webp';
    import videoCoverImage from '@/assets/images/cars/video-cover.webp';

    import { computed, h, inject, onMounted, ref } from 'vue';
    import { useRoute, useRouter } from 'vue-router';

    import { ModalPlugin } from '@/plugins/modalPlugin.ts';

    import {
        Car,
        CarDocument,
        CarDocumentType,
        CarPhoto,
        CarRejectForm,
        CarStatus,
        CarVideo,
        getCarAdmissionTypeLabel,
        getCarNdsTypeLabel,
    } from '@/types/car.type.ts';

    import useCarRepository from '@/repositories/car/useCarRepository.ts';
    import useDocumentDownloader from '@/utils/useDocumentDownloader.ts';
    import useLoading from '@/utils/useLoading.ts';
    import useNotifications from '@/utils/useNotifications.ts';
    import usePermission from '@/repositories/auth/usePermission.ts';

    import { formatFromUTC } from '@/utils/DateTimeFormatter.ts';
    import { numberFormat } from '@/utils/NumberFormatter.ts';
    import { getCarDealerFullName, getCarDocuments } from '@/utils/car';

    import UIAccordionList from '@/components/ui/UIAccordionList/UIAccordionList.vue';
    import UIDropDown from '@/components/ui/UIDropDown/UIDropDown.vue';
    import UIGalleryWithSlider from '@/components/ui/UIGalleryWithSlider/UIGalleryWithSlider.vue';
    import UILoader from '@/components/ui/UILoader/UILoader.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    import ConfirmActionModal from '@/components/modals/ConfirmActionModal/ConfirmActionModal.vue';

    import CarCharacteristics from '@/components/cars/CarCharacteristics/CarCharacteristics.vue';
    import CarConfirmApprove from '@/components/cars/CarConfirmApprove/CarConfirmApprove.vue';
    import CarConfirmDelete from '@/components/cars/CarConfirmDelete/CarConfirmDelete.vue';
    import CarConfirmRecreate from '@/components/cars/CarConfirmRecreate/CarConfirmRecreate.vue';
    import CarConfirmReject from '@/components/cars/CarConfirmReject/CarConfirmReject.vue';
    import CarEquipment from '@/components/cars/CarEquipment/CarEquipment.vue';
    import CarDamage from '@/components/cars/CarDamage/CarDamage.vue';
    import CarDocuments from '@/components/cars/CarDocuments/CarDocuments.vue';
    import CarLegal from '@/components/cars/CarLegal/CarLegal.vue';

    import ViewCarHeader from '@/views/cars/ViewCar/components/ViewCarHeader/ViewCarHeader.vue';

    const $modal = inject('$modal') as typeof ModalPlugin;

    const router = useRouter();
    const route = useRoute();
    const { getCar, deleteCar, recreateCar, approveCar, rejectCar } = useCarRepository();
    const { canApproveCar, canRejectCar, canViewCarSaleContract, canViewCarInvoice } = usePermission();
    const { downloadAuctionInvoice, downloadAuctionSaleContract } = useDocumentDownloader();
    const { showSuccess } = useNotifications();
    const { isLoading: isCarLoading, setLoading: setCarLoading } = useLoading();

    const car = ref<Car | null>(null);
    const characteristics = ref([
        { title: 'Характеристики', content: '' },
        { title: 'Комплектация', content: '' },
        { title: 'Диагностика и повреждения', content: '' },
        { title: 'Юридическая чистота', content: '' },
        { title: 'Документы', content: '' },
    ]);

    const carDocuments = computed<Record<CarDocumentType, CarDocument[]>>(() => {
        return getCarDocuments(car?.value !== null ? car?.value : undefined);
    });

    const galleryItems = computed(() => {
        if (car.value === null) {
            return [{
                src: noPhotoImage,
                thumb: noPhotoImage,
                type: 'image',
            }];
        }

        const items: {
            src: string,
            thumb: string,
            type?: string,
            content_type?: string,
        }[] = [];

        car.value?.photos?.forEach((photo: CarPhoto) => {
            items.push({
                src: photo.path,
                thumb: photo.path_small,
                type: 'image',
            });
        });

        car.value?.videos?.forEach((video: CarVideo) => {
            items.push({
                src: video.path,
                thumb: videoCoverImage,
                type: 'video',
                content_type: video.content_type,
            });
        });

        return items;
    });

    const carId = route.params.car_id as unknown as number;

    function handleDeleteCar(): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    car.value ? h(CarConfirmDelete, { car: car.value }) : '',
            },
            props: {
                onConfirm: async () => {
                    await deleteCar(carId);

                    showSuccess({
                        title: 'Автомобиль удален',
                    });

                    router.push({ name: 'CarsList' });
                },
            },
        });
    }

    function handleRecreateCar(): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    car.value ? h(CarConfirmRecreate, { car: car.value }) : '',
            },
            props: {
                onConfirm: async () => {
                    const recreatedCarId = await recreateCar(carId);

                    showSuccess({
                        title: 'Автомобиль пересоздан',
                    });

                    router.push({ name: 'ViewCar', params: { car_id: recreatedCarId } });
                },
            },
        });
    }

    function handleApproveCar(): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    car.value ? h(CarConfirmApprove, { car: car.value }) : '',
            },
            props: {
                onConfirm: async (): Promise<unknown> => {
                    try {
                        await approveCar(carId);
                    }
                    catch (errors) {
                        return errors;
                    }

                    showSuccess({
                        title: 'Автомобиль согласован',
                    });

                    fetchCar();
                },
            },
        });
    }

    function handleRejectCar(): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    car.value ? h(CarConfirmReject, { car: car.value }) : '',
            },
            props: {
                onConfirm: async (rejectForm: CarRejectForm): Promise<unknown> => {
                    try {
                        await rejectCar(carId, rejectForm.comment);
                    }
                    catch (errors) {
                        return errors;
                    }

                    showSuccess({
                        title: 'Автомобиль отклонен',
                    });

                    fetchCar();
                },
            },
        });
    }

    function onDownloadSaleContract(): void {
        if (car.value?.lastAuction?.last_lot?.id) {
            downloadAuctionSaleContract(car.value?.lastAuction.last_lot.id, `auction-${car.value?.lastAuction.last_lot.id}-sale-contract.docx`);
        }
    }

    function onDownloadInvoice(): void {
        if (car.value?.lastAuction?.last_lot?.id) {
            downloadAuctionInvoice(car.value?.lastAuction.last_lot.id, `auction-${car.value?.lastAuction.last_lot.id}-invoice.xlsx`);
        }
    }

    function fetchCar(): void {
        setCarLoading(true);

        getCar(carId).then((fetchedCar: Car) => {
            car.value = fetchedCar;
            setCarLoading(false);
        });
    }

    onMounted(() => {
        fetchCar();
    });
</script>

<style lang="scss">
    @use './ViewCar.scss' as *;
</style>
